import type { TrackingParameters } from "@/modules/corporate/constants"
import type { Page } from "@/modules/corporate/types/pages"
import type { ResolvedRichTextValue } from "@/modules/kontent/types"
import type { NextRouter } from "next/router"
import type { ChangeEvent, FormEvent, FunctionComponent } from "react"

import { useEffect, useState } from "react"

import { ElementMessage } from "@jobteaser/ui-kit/components//ElementMessage/ElementMessage"
import { Button, BUTTON_TYPES } from "@jobteaser/ui-kit/components/Button"
import { InputField } from "@jobteaser/ui-kit/components/Input/InputField"
import { Text, TEXT_ALIGNS, TEXT_TYPES } from "@jobteaser/ui-kit/components/Text/Text"

import { InternalLink } from "@/modules/corporate/components/InternalLink"
import {
  LOCAL_STORAGE_FORM_DATA_KEY,
  SESSION_STORAGE_CAMPAIGN_TRACKING_DATA_KEY,
  SESSION_STORAGE_REFERRER_TRACKING_KEY,
} from "@/modules/corporate/constants"
import { fetcher } from "@/modules/fetch/fetcher"
import { Translation } from "@/modules/i18n/components/Translation"
import { useTranslation } from "@/modules/i18n/components/useTranslation"
import { RichText } from "@/modules/kontent/components/RichText"
import { useLocale } from "@/modules/locales/useLocale"
import { useRouter } from "@/modules/routing/hooks/useRouter"
import { SelectField } from "@/modules/uiKitOverrides/SelectFilter/SelectField"

import {
  cleanLocalStorage,
  DIAL_CODE_OPTIONS,
  DIAL_CODES,
  getOptionFromValue,
  handleInputFieldChange,
  handleSelectFieldChange,
  LANGUAGE_VALUES,
} from "./utils"

import styles from "./Contact.module.css"

const ENTERPRISE_SIZE_OPTIONS = [
  { label: "1-10", value: "1-10" },
  { label: "11-50", value: "11-50" },
  { label: "51-100", value: "51-100" },
  { label: "101-250", value: "101-250" },
  { label: "251-2000", value: "251-2000" },
  { label: ">2000", value: "> 2000" },
]

type RecruiterContactProps = {
  actionUrl: string
  conditionsPage: Page
  footer: ResolvedRichTextValue
  redirection: string
}

type RecruiterContactFormFieldId =
  // Visible fields
  | "company"
  | "country"
  | "dial_code"
  | "email"
  | "Enterprise_size"
  | "first_name"
  | "How_did_you_hear_about_us"
  | "job_title"
  | "Language"
  | "Last_interaction_date"
  // Hidden fields
  | "last_name"
  | "Pays"
  | "phone"
  | "Sector_Ops"
  | TrackingParameters

async function handleSubmit({
  actionUrl,
  event,
  formState,
  push,
  redirection,
}: {
  actionUrl: string
  event: FormEvent<HTMLFormElement>
  formState: Partial<Record<RecruiterContactFormFieldId, string>>
  push: NextRouter["push"]
  redirection: string
}): Promise<boolean> {
  event.preventDefault()

  const notFilledFields: RecruiterContactFormFieldId[] = [
    "company",
    "dial_code",
    "email",
    "Enterprise_size",
    "first_name",
    "How_did_you_hear_about_us",
    "job_title",
    "last_name",
    "phone",
    "Sector_Ops",
  ].filter(field => !formState[field as RecruiterContactFormFieldId]) as RecruiterContactFormFieldId[]

  if (notFilledFields.length) {
    return false
  }

  const sessionStorageCampaignTrackingData =
    window.sessionStorage.getItem(SESSION_STORAGE_CAMPAIGN_TRACKING_DATA_KEY) || ""
  const urlSearchParams = new URLSearchParams(sessionStorageCampaignTrackingData)
  const trackingFields: Record<TrackingParameters, string> = {
    gclid: `${urlSearchParams.get("gclid") || ""}`,
    msclkid: `${urlSearchParams.get("msclkid") || ""}`,
    sfcid: `${urlSearchParams.get("sfcid") || ""}`,
    utm_campaign: `${urlSearchParams.get("utm_campaign") || ""}`,
    utm_content: `${urlSearchParams.get("utm_content") || ""}`,
    utm_medium: `${urlSearchParams.get("utm_medium") || ""}`,
    utm_source: `${urlSearchParams.get("utm_source") || ""}`,
    utm_term: `${urlSearchParams.get("utm_term") || ""}`,
  }
  const referer = window.sessionStorage.getItem(SESSION_STORAGE_REFERRER_TRACKING_KEY) || "jobteaser"

  const { dial_code: dialCode, phone, ...postData } = formState
  const data = {
    ...postData,
    ...trackingFields,
    actionUrl,
    phone: `${dialCode}${phone}`,
    referer,
  }
  const body = JSON.stringify(data)
  const encodedFormData = window.btoa(encodeURIComponent(body))

  const { status } = await fetcher(`/api/corporate/contact-form`, {
    requestInit: {
      body,
      method: "POST",
    },
    serviceDomain: "INTERNAL_NEXT_API",
  })
    .then(res => res.json())
    .catch(() => ({ status: false }))

  if (status) {
    window.localStorage.setItem(LOCAL_STORAGE_FORM_DATA_KEY, encodedFormData)
    window.sessionStorage.removeItem(SESSION_STORAGE_CAMPAIGN_TRACKING_DATA_KEY)

    push(redirection).catch(() => null)
  }

  return status
}

export const RecruiterContact: FunctionComponent<RecruiterContactProps> = ({
  actionUrl,
  conditionsPage,
  footer,
  redirection,
}) => {
  const { push } = useRouter()
  const locale = useLocale()
  const { t } = useTranslation(["corporate_form"])
  const industryOptions = [
    { label: t("corporate_form.recruiter.industry.options.1"), value: "Accounting, Auditing & Management Consulting" },
    { label: t("corporate_form.recruiter.industry.options.2"), value: "Advertising & Market Research Services" },
    { label: t("corporate_form.recruiter.industry.options.3"), value: "Banking, Insurance & Investment" },
    { label: t("corporate_form.recruiter.industry.options.4"), value: "Civil Engineering & Architecture" },
    { label: t("corporate_form.recruiter.industry.options.5"), value: "Computer, Programming & Software" },
    { label: t("corporate_form.recruiter.industry.options.6"), value: "Food & Beverage Production" },
    { label: t("corporate_form.recruiter.industry.options.7"), value: "Human Ressources & Recruitment Services" },
    { label: t("corporate_form.recruiter.industry.options.8"), value: "Information Technology & Telecommunication" },
    { label: t("corporate_form.recruiter.industry.options.9"), value: "Media" },
    { label: t("corporate_form.recruiter.industry.options.10"), value: "Pharmaceutical Industry" },
    { label: t("corporate_form.recruiter.industry.options.11"), value: "Specialized, Scientific & Technical services" },
    { label: t("corporate_form.recruiter.industry.options.12"), value: "Wholesale & Retail Trade" },
    { label: t("corporate_form.recruiter.industry.options.13"), value: "Not found" },
  ]
  const howDidYouHearAboutUsOptions = [
    { label: t("corporate_form.recruiter.How_did_you_hear_about_us.options.1"), value: "Social media" },
    { label: t("corporate_form.recruiter.How_did_you_hear_about_us.options.2"), value: "Event" },
    {
      label: t("corporate_form.recruiter.How_did_you_hear_about_us.options.3"),
      value: "Online research (e.g. Google)",
    },
    { label: t("corporate_form.recruiter.How_did_you_hear_about_us.options.4"), value: "External recommendation" },
    // { label: t("corporate_form.recruiter.How_did_you_hear_about_us.options.5"), value: "Previous company client JT" },
    {
      label: t("corporate_form.recruiter.How_did_you_hear_about_us.options.6"),
      value: "Previous user of JT career center as student",
    },
    { label: t("corporate_form.recruiter.How_did_you_hear_about_us.options.7"), value: "Already JT Customer" },
    { label: t("corporate_form.recruiter.How_did_you_hear_about_us.options.8"), value: "Free version" },
    { label: t("corporate_form.recruiter.How_did_you_hear_about_us.options.9"), value: "Advertising" },
    { label: t("corporate_form.recruiter.How_did_you_hear_about_us.options.10"), value: "Don’t remember" },
  ]
  const [hasError, setHasError] = useState(false)
  const [formState, setFormState] = useState<Partial<Record<RecruiterContactFormFieldId, string>>>({
    country: "",
    Language: LANGUAGE_VALUES[locale],
    Last_interaction_date: "",
    Pays: "",
  })

  const onSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    const status = await handleSubmit({ actionUrl, event, formState, push, redirection })
    if (!status) {
      setHasError(true)
    }
  }

  useEffect(() => {
    cleanLocalStorage()
  }, [])

  useEffect(() => {
    setHasError(false)
  }, [formState])

  return (
    <>
      <form method="POST" onSubmit={(event: FormEvent<HTMLFormElement>) => void onSubmit(event)}>
        {hasError && (
          <ElementMessage.Error
            onButtonClose={() => setHasError(false)}
            title={t("corporate_form.recruiter.errors.title")}
            withCloseButton
          >
            {t("corporate_form.recruiter.errors.message")}
          </ElementMessage.Error>
        )}
        <div className={styles.groupFieldset}>
          <InputField
            commonProps={{
              hasError: hasError && !formState.first_name,
            }}
            fieldProps={{
              id: "first_name",
              onChange: (event: ChangeEvent<HTMLInputElement>) =>
                handleInputFieldChange({ event, formState, formUpdater: setFormState }),
              placeholder: t("corporate_form.recruiter.first_name.placeholder"),
              value: formState.first_name,
            }}
            labelProps={{
              label: t("corporate_form.recruiter.first_name.label"),
            }}
          />
          <InputField
            commonProps={{
              hasError: hasError && !formState.last_name,
            }}
            fieldProps={{
              id: "last_name",
              onChange: (event: ChangeEvent<HTMLInputElement>) =>
                handleInputFieldChange({ event, formState, formUpdater: setFormState }),
              placeholder: t("corporate_form.recruiter.last_name.placeholder"),
              value: formState.last_name,
            }}
            labelProps={{
              label: t("corporate_form.recruiter.last_name.label"),
            }}
          />
        </div>
        <div className={styles.groupFieldset}>
          <InputField
            commonProps={{
              hasError: hasError && !formState.email,
            }}
            fieldProps={{
              id: "email",
              onChange: (event: ChangeEvent<HTMLInputElement>) =>
                handleInputFieldChange({ event, formState, formUpdater: setFormState }),
              placeholder: t("corporate_form.recruiter.email.placeholder"),
              type: "email",
              value: formState.email,
            }}
            labelProps={{
              label: t("corporate_form.recruiter.email.label"),
            }}
          />
          <div className={styles.subGroupFieldset}>
            <SelectField
              commonProps={{
                hasError: hasError && !formState.dial_code,
              }}
              fieldProps={{
                id: "dial_code",
                onChange: (data: { label: string; value: string } | null) => {
                  const country = data && DIAL_CODES.find(({ name }) => name === data.value)
                  handleSelectFieldChange({
                    data: data && country ? { label: data.value, value: country ? country.dial_code : "" } : null,
                    formState: country ? { ...formState, country: country.name, Pays: country.name } : formState,
                    formUpdater: setFormState,
                    name: "dial_code",
                  })
                },
                options: DIAL_CODE_OPTIONS,
                required: true,
                value: getOptionFromValue(DIAL_CODE_OPTIONS, formState.Pays),
              }}
              labelProps={{
                label: t("corporate_form.recruiter.phone_prefix.label"),
              }}
            />
            <InputField
              commonProps={{
                hasError: hasError && !formState.phone,
              }}
              fieldProps={{
                id: "phone",
                onChange: (event: ChangeEvent<HTMLInputElement>) =>
                  handleInputFieldChange({ event, formState, formUpdater: setFormState }),
                // Won't work thanks to UI-kit
                pattern: "[0-9]+",
                placeholder: t("corporate_form.recruiter.phone.placeholder"),
                type: "tel",
                value: formState.phone,
              }}
              labelProps={{
                label: t("corporate_form.recruiter.phone.label"),
              }}
            />
          </div>
        </div>
        <div className={styles.groupFieldset}>
          <InputField
            commonProps={{
              hasError: hasError && !formState.job_title,
            }}
            fieldProps={{
              id: "job_title",
              onChange: (event: ChangeEvent<HTMLInputElement>) =>
                handleInputFieldChange({ event, formState, formUpdater: setFormState }),
              placeholder: t("corporate_form.recruiter.job_title.placeholder"),
              value: formState.job_title,
            }}
            labelProps={{
              label: t("corporate_form.recruiter.job_title.label"),
            }}
          />
          <InputField
            commonProps={{
              hasError: hasError && !formState.company,
            }}
            fieldProps={{
              id: "company",
              onChange: (event: ChangeEvent<HTMLInputElement>) =>
                handleInputFieldChange({ event, formState, formUpdater: setFormState }),
              placeholder: t("corporate_form.recruiter.company.placeholder"),
              value: formState.company,
            }}
            labelProps={{
              label: t("corporate_form.recruiter.company.label"),
            }}
          />
        </div>
        <div className={styles.groupFieldset}>
          <SelectField
            commonProps={{
              hasError: hasError && !formState.Enterprise_size,
            }}
            fieldProps={{
              id: "Enterprise_size",
              onChange: (data: { label: string; value: string } | null) =>
                handleSelectFieldChange({ data, formState, formUpdater: setFormState, name: "Enterprise_size" }),
              options: ENTERPRISE_SIZE_OPTIONS,
              required: true,
              value: getOptionFromValue(ENTERPRISE_SIZE_OPTIONS, formState.Enterprise_size),
            }}
            labelProps={{
              label: t("corporate_form.recruiter.Enterprise_size.label"),
            }}
          />
          <SelectField
            commonProps={{
              hasError: hasError && !formState.Sector_Ops,
            }}
            fieldProps={{
              id: "Sector_Ops",
              onChange: (data: { label: string; value: string } | null) =>
                handleSelectFieldChange({ data, formState, formUpdater: setFormState, name: "Sector_Ops" }),
              options: industryOptions,
              value: getOptionFromValue(industryOptions, formState.Sector_Ops),
            }}
            labelProps={{
              label: t("corporate_form.recruiter.industry.label"),
            }}
          />
        </div>
        <SelectField
          commonProps={{
            hasError: hasError && !formState.How_did_you_hear_about_us,
          }}
          fieldProps={{
            id: "How_did_you_hear_about_us",
            onChange: (data: { label: string; value: string } | null) =>
              handleSelectFieldChange({
                data,
                formState,
                formUpdater: setFormState,
                name: "How_did_you_hear_about_us",
              }),
            options: howDidYouHearAboutUsOptions,
            required: true,
            value: getOptionFromValue(howDidYouHearAboutUsOptions, formState.How_did_you_hear_about_us),
          }}
          labelProps={{
            label: t("corporate_form.recruiter.How_did_you_hear_about_us.label"),
          }}
        />
        <Text className={styles.conditions} resetSpacing type={TEXT_TYPES.SMALL}>
          <Translation
            i18nKey="corporate_form.recruiter.conditions"
            link={
              <InternalLink className={styles.link} page={conditionsPage} target="_blank">
                {t("corporate_form.recruiter.conditions_link_title")}
              </InternalLink>
            }
          />
        </Text>
        <Button className={styles.button} isFullWidth type={BUTTON_TYPES.SUBMIT}>
          {t("corporate_form.buttons.submit")}
        </Button>
      </form>
      {footer && (
        <RichText
          value={footer}
          wrapperProps={{
            align: TEXT_ALIGNS.CENTER,
            className: styles.footer,
            resetSpacing: true,
            tag: "div",
          }}
        />
      )}
    </>
  )
}
